const RECURRING_INTERVALS = {
  weekly: "weekly",
  monthly: "monthly",
  yearly: "yearly",
};

const MAX_OCCURRENCES_PER_LOAD = 24;
const MAX_START_DAYS_BACK = 366;
const MAX_START_DAYS_AHEAD = 366;
const MIN_DATE_KEY = "2000-01-01";

const MS_PER_DAY = 86_400_000;
const MS_PER_HOUR = 3_600_000;
const DATE_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;

const isRecurringInterval = (value) =>
  Object.values(RECURRING_INTERVALS).includes(value);

const pad = (value, length = 2) => String(value).padStart(length, "0");

const toDateKey = (year, month, day) => `${pad(year, 4)}-${pad(month)}-${pad(day)}`;

const daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();

const parseDateKey = (value) => {
  const match = typeof value === "string" ? DATE_KEY.exec(value) : null;
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
    return null;
  }
  return { year, month, day };
};

const isDateKey = (value) => parseDateKey(value) !== null;

const utcNoonOf = (key) => {
  const { year, month, day } = parseDateKey(key);
  return Date.UTC(year, month - 1, day, 12);
};

const keyOfUtc = (time) => new Date(time).toISOString().slice(0, 10);

const addDays = (key, days) => keyOfUtc(utcNoonOf(key) + days * MS_PER_DAY);

const daysBetween = (fromKey, toKey) =>
  Math.round((utcNoonOf(toKey) - utcNoonOf(fromKey)) / MS_PER_DAY);

const clampedDate = (year, month, day) =>
  toDateKey(year, month, Math.min(day, daysInMonth(year, month)));

const monthIndexOf = ({ year, month }) => year * 12 + (month - 1);

const occurrenceDate = (startKey, interval, index) => {
  const start = parseDateKey(startKey);
  if (interval === RECURRING_INTERVALS.weekly) return addDays(startKey, index * 7);
  if (interval === RECURRING_INTERVALS.yearly) {
    return clampedDate(start.year + index, start.month, start.day);
  }
  const monthIndex = monthIndexOf(start) + index;
  return clampedDate(Math.floor(monthIndex / 12), (monthIndex % 12) + 1, start.day);
};

const roughIndexOf = (startKey, interval, key) => {
  if (interval === RECURRING_INTERVALS.weekly) {
    return Math.floor(daysBetween(startKey, key) / 7);
  }
  const start = parseDateKey(startKey);
  const date = parseDateKey(key);
  if (interval === RECURRING_INTERVALS.yearly) return date.year - start.year;
  return monthIndexOf(date) - monthIndexOf(start);
};

const nextOccurrenceAfter = (startKey, interval, key) => {
  let index = Math.max(0, roughIndexOf(startKey, interval, key));
  while (occurrenceDate(startKey, interval, index) <= key) index += 1;
  return occurrenceDate(startKey, interval, index);
};

const firstOccurrenceOnOrAfter = (startKey, interval, key) =>
  nextOccurrenceAfter(startKey, interval, addDays(key, -1));

const lastSundayOf = (year, month) => {
  const last = new Date(Date.UTC(year, month, 0));
  return last.getUTCDate() - last.getUTCDay();
};

// Europe/Berlin without ICU time zone data: CEST from the last Sunday of March
// to the last Sunday of October, 01:00 UTC each (EU rule since 1996).
const isBerlinSummerTime = (time) => {
  const year = new Date(time).getUTCFullYear();
  const begins = Date.UTC(year, 2, lastSundayOf(year, 3), 1);
  const ends = Date.UTC(year, 9, lastSundayOf(year, 10), 1);
  return time >= begins && time < ends;
};

const berlinOffsetMs = (time) => (isBerlinSummerTime(time) ? 2 : 1) * MS_PER_HOUR;

const berlinDateKey = (time) => keyOfUtc(time + berlinOffsetMs(time));

const berlinTimeOn = (key, hour) => {
  const { year, month, day } = parseDateKey(key);
  const wallClock = Date.UTC(year, month - 1, day, hour);
  const guess = wallClock - berlinOffsetMs(wallClock);
  return wallClock - berlinOffsetMs(guess);
};

const berlinMidnight = (key) => berlinTimeOn(key, 0);

const OCCURRENCE_HOUR = 12;

const berlinNoon = (key) => berlinTimeOn(key, OCCURRENCE_HOUR);

const isEnded = (endKey, key) => Boolean(endKey) && key > endKey;

const dueOccurrences = (
  { startDate, interval, endDate = null, nextDate },
  now,
  limit = MAX_OCCURRENCES_PER_LOAD,
) => {
  const today = berlinDateKey(now);
  const dates = [];
  let next = nextDate;
  while (next && next <= today && !isEnded(endDate, next) && dates.length < limit) {
    dates.push(next);
    next = nextOccurrenceAfter(startDate, interval, next);
  }
  return { dates, nextDate: next && !isEnded(endDate, next) ? next : null };
};

const scheduledNextDate = ({ startDate, interval, endDate = null, lastDue = null }, now) => {
  const today = berlinDateKey(now);
  const after = lastDue && lastDue >= today ? addDays(lastDue, 1) : today;
  const next = firstOccurrenceOnOrAfter(startDate, interval, after < startDate ? startDate : after);
  return isEnded(endDate, next) ? null : next;
};

const initialNextDate = ({ startDate, endDate = null }) =>
  isEnded(endDate, startDate) ? null : startDate;

const startDateBounds = (now) => {
  const today = berlinDateKey(now);
  return {
    earliest: addDays(today, -MAX_START_DAYS_BACK),
    latest: addDays(today, MAX_START_DAYS_AHEAD),
  };
};

module.exports = {
  RECURRING_INTERVALS,
  MAX_OCCURRENCES_PER_LOAD,
  MAX_START_DAYS_BACK,
  MAX_START_DAYS_AHEAD,
  MIN_DATE_KEY,
  isRecurringInterval,
  parseDateKey,
  isDateKey,
  daysInMonth,
  addDays,
  daysBetween,
  occurrenceDate,
  nextOccurrenceAfter,
  firstOccurrenceOnOrAfter,
  berlinDateKey,
  berlinMidnight,
  berlinNoon,
  dueOccurrences,
  scheduledNextDate,
  initialNextDate,
  startDateBounds,
};
